import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { 
  CheckCircle, XCircle, Clock, AlertTriangle, Search, RotateCcw, MapPin, Calendar as CalendarIcon, 
  User, Users, DollarSign, FileText, Building2, Filter, ChevronRight, Eye, History, 
  AlertCircle, Loader2, MapPinOff, Timer, Edit3, GitBranch
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { ShiftPeriodBadge, autoClassifyPeriod } from '@/components/calendar/ShiftPeriodBadge';
import FundingPreviewBanner from '@/components/timesheets/FundingPreviewBanner';
import { SubmissionSourceBadge } from '@/components/timesheets/SubmissionSourceBadge';
import TodaysSubmissionsWidget from '@/components/timesheets/TodaysSubmissionsWidget';
import { PayrollBadges } from '@/components/timesheets/PayrollStatusBadges';

// ═══════════════════════════════════════════════════════════════════════════════
// BADGE CONFIGURATIONS
// ═══════════════════════════════════════════════════════════════════════════════

const ATTENDANCE_BADGE = {
  on_time: { label: 'On Time', className: 'bg-green-500 text-white' },
  late_sign_in: { label: 'Late Sign In', className: 'bg-yellow-500 text-black' },
  early_sign_out: { label: 'Early Sign Out', className: 'bg-yellow-500 text-black' },
  late_and_early: { label: 'Late & Early', className: 'bg-orange-500 text-white' },
  missing_clock_in: { label: 'Missing Clock In', className: 'bg-red-500 text-white' },
  missing_clock_out: { label: 'Missing Clock Out', className: 'bg-red-500 text-white' },
  missing_both_clocks: { label: 'No Attendance', className: 'bg-red-700 text-white' },
  overtime: { label: 'Overtime', className: 'bg-blue-500 text-white' },
  attendance_exception: { label: 'Exception', className: 'bg-red-500 text-white' },
};

const APPROVAL_BADGE = {
  pending_approval: { label: 'Pending Approval', className: 'bg-yellow-500 text-black' },
  approved_scheduled: { label: 'Approved (Scheduled)', className: 'bg-green-600 text-white' },
  approved_adjusted: { label: 'Approved (Adjusted)', className: 'bg-green-500 text-white' },
  rejected: { label: 'Rejected', className: 'bg-red-600 text-white' },
  returned_for_correction: { label: 'Returned', className: 'bg-orange-500 text-white' },
  superseded: { label: 'Superseded', className: 'bg-gray-500 text-white' },
  // Day 17: Post-Completion Correction Statuses
  corrected_pending_reapproval: { label: 'Needs Reapproval', className: 'bg-orange-600 text-white' },
  reapproved: { label: 'Reapproved', className: 'bg-green-600 text-white' },
  DRAFT: { label: 'Draft', className: 'bg-gray-500 text-white' },
  SUBMITTED: { label: 'Submitted', className: 'bg-yellow-500 text-black' },
  APPROVED: { label: 'Approved', className: 'bg-green-600 text-white' },
  REJECTED: { label: 'Rejected', className: 'bg-red-600 text-white' },
};

const EXCEPTION_TYPES = [
  { value: 'late_sign_in', label: 'Late Sign In' },
  { value: 'early_sign_out', label: 'Early Sign Out' },
  { value: 'late_and_early', label: 'Late & Early Leave' },
  { value: 'missing_clock_in', label: 'Missing Clock In' },
  { value: 'missing_clock_out', label: 'Missing Clock Out' },
  { value: 'missing_both_clocks', label: 'Missing Both Clocks' },
  { value: 'overtime', label: 'Overtime' },
  { value: 'attendance_exception', label: 'Attendance Exception' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

const formatTime = (isoString) => {
  if (!isoString) return '-';
  try {
    return new Date(isoString).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoString;
  }
};

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  try {
    return new Date(isoString).toLocaleString('en-AU', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit' 
    });
  } catch {
    return isoString;
  }
};

const formatMinutes = (mins) => {
  if (!mins && mins !== 0) return '-';
  const h = Math.floor(Math.abs(mins) / 60);
  const m = Math.abs(mins) % 60;
  const sign = mins < 0 ? '-' : '';
  if (h > 0) return `${sign}${h}h ${m}m`;
  return `${sign}${m}m`;
};

// ═══════════════════════════════════════════════════════════════════════════════
// BADGE COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const AttendanceBadge = ({ status }) => {
  const config = ATTENDANCE_BADGE[status] || { label: status || 'Unknown', className: 'bg-gray-500 text-white' };
  return (
    <Badge className={`${config.className} text-xs`} data-testid="attendance-badge">
      {config.label}
    </Badge>
  );
};

const ApprovalBadge = ({ status }) => {
  const config = APPROVAL_BADGE[status] || { label: status || 'Unknown', className: 'bg-gray-500 text-white' };
  return (
    <Badge className={`${config.className} text-xs`} data-testid="approval-badge">
      {config.label}
    </Badge>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// EXCEPTION INDICATOR COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const ExceptionIndicators = ({ timesheet }) => {
  const indicators = [];
  
  if (timesheet.late_minutes > 0) {
    indicators.push(
      <span key="late" className="text-yellow-600 text-xs" title={`Late by ${timesheet.late_minutes}m`}>
        <Clock className="h-3 w-3 inline mr-0.5" />Late
      </span>
    );
  }
  if (timesheet.early_leave_minutes > 0) {
    indicators.push(
      <span key="early" className="text-orange-600 text-xs" title={`Left early by ${timesheet.early_leave_minutes}m`}>
        <Timer className="h-3 w-3 inline mr-0.5" />Early
      </span>
    );
  }
  if (timesheet.overtime_minutes > 0) {
    indicators.push(
      <span key="ot" className="text-blue-600 text-xs" title={`Overtime: ${timesheet.overtime_minutes}m`}>
        <Timer className="h-3 w-3 inline mr-0.5" />OT
      </span>
    );
  }
  if (timesheet.gps_issue || timesheet.gps_mismatch) {
    indicators.push(
      <span key="gps" className="text-red-600 text-xs" title="GPS Issue">
        <MapPinOff className="h-3 w-3 inline mr-0.5" />GPS
      </span>
    );
  }
  if (timesheet.manual_edit || timesheet.times_manually_updated) {
    indicators.push(
      <span key="manual" className="text-purple-600 text-xs" title="Manually Edited">
        <FileText className="h-3 w-3 inline mr-0.5" />Edited
      </span>
    );
  }
  
  if (indicators.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="exception-indicators">
      {indicators}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DATE RANGE PICKER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const DateRangePicker = ({ dateFrom, dateTo, onDateFromChange, onDateToChange }) => {
  return (
    <div className="flex items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="w-[130px] justify-start text-left font-normal">
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateFrom ? format(new Date(dateFrom), 'dd MMM') : 'From'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={dateFrom ? new Date(dateFrom) : undefined}
            onSelect={(date) => onDateFromChange(date ? format(date, 'yyyy-MM-dd') : '')}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <span className="text-muted-foreground">-</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="w-[130px] justify-start text-left font-normal">
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateTo ? format(new Date(dateTo), 'dd MMM') : 'To'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={dateTo ? new Date(dateTo) : undefined}
            onSelect={(date) => onDateToChange(date ? format(date, 'yyyy-MM-dd') : '')}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {(dateFrom || dateTo) && (
        <Button variant="ghost" size="sm" onClick={() => { onDateFromChange(''); onDateToChange(''); }}>
          Clear
        </Button>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DETAIL DRAWER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const TimesheetDetailDrawer = ({ 
  timesheet, 
  open, 
  onClose, 
  onApproveScheduled, 
  onApproveAdjusted, 
  onReject, 
  onReturn,
  onUpdateTimes,
  actionLoading 
}) => {
  const [adjustedHours, setAdjustedHours] = useState('');
  const [notes, setNotes] = useState('');
  const [actualClockIn, setActualClockIn] = useState('');
  const [actualClockOut, setActualClockOut] = useState('');
  const [editingTimes, setEditingTimes] = useState(false);
  const [editClockIn, setEditClockIn] = useState('');
  const [editClockOut, setEditClockOut] = useState('');
  const [actionMode, setActionMode] = useState(null); // 'adjusted', 'reject', 'return', 'update-times', 'edit-after-approval', 'reapprove'
  // Day 17: Edit after approval state
  const [editHours, setEditHours] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if (timesheet) {
      setAdjustedHours(timesheet.base_duration_hours?.toString() || timesheet.hours_worked?.toString() || '');
      setNotes('');
      setActualClockIn(timesheet.actual_clock_in_time || '');
      setActualClockOut(timesheet.actual_clock_out_time || '');
      setActionMode(null);
      // Day 17: Reset edit state
      setEditHours(timesheet.hours_worked?.toString() || '');
      setEditReason('');
    }
  }, [timesheet]);

  if (!timesheet) return null;

  const canApprove = ['pending_approval', 'DRAFT', 'SUBMITTED', 'returned_for_correction', 
    'approved_scheduled', 'approved_adjusted', 'reapproved', 'APPROVED', 'corrected_pending_reapproval'].includes(
    timesheet.approval_status || timesheet.status
  );

  const handleAction = async (action) => {
    switch (action) {
      case 'scheduled':
        await onApproveScheduled(timesheet.id, notes);
        break;
      case 'adjusted':
        const hours = parseFloat(adjustedHours);
        if (isNaN(hours) || hours <= 0) {
          notify.warn('VALIDATION_ADJUSTED_HOURS');
          return;
        }
        await onApproveAdjusted(timesheet.id, hours, notes);
        break;
      case 'reject':
        if (!notes.trim()) {
          notify.warn('VALIDATION_REJECTION_REASON');
          return;
        }
        await onReject(timesheet.id, notes);
        break;
      case 'return':
        if (!notes.trim()) {
          notify.warn('VALIDATION_CORRECTION_NOTES');
          return;
        }
        await onReturn(timesheet.id, notes);
        break;
      case 'update-times':
        await onUpdateTimes(timesheet.id, actualClockIn, actualClockOut, notes);
        break;
      // Day 17: Edit after approval
      case 'edit-after-approval':
        if (editReason.length < 10) {
          notify.warn('VALIDATION_EDIT_REASON');
          return;
        }
        setEditLoading(true);
        try {
          const payload = { edit_reason: editReason };
          if (editHours && parseFloat(editHours) !== timesheet.hours_worked) {
            payload.hours_worked = parseFloat(editHours);
          }
          if (notes) payload.notes = notes;
          await api.post(`/corrections/timesheets/${timesheet.id}/edit-after-approval`, payload);
          toast.success('Timesheet edited. Reapproval required.');
          onClose();
        } catch (e) {
          notify.error(e, 'TIMESHEET_EDIT_FAILED');
        } finally {
          setEditLoading(false);
        }
        break;
      // Day 17: Reapprove
      case 'reapprove':
        setEditLoading(true);
        try {
          await api.post(`/corrections/timesheets/${timesheet.id}/reapprove`, {
            approval_notes: notes,
          });
          toast.success('Timesheet reapproved successfully');
          onClose();
        } catch (e) {
          notify.error(e, 'TIMESHEET_REAPPROVAL_FAILED');
        } finally {
          setEditLoading(false);
        }
        break;
    }
    setActionMode(null);
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="timesheet-detail-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Timesheet Review
          </SheetTitle>
          <SheetDescription>
            {timesheet.date} • {timesheet.staff_name}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Section A: Shift Details */}
          <Card data-testid="shift-details-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" /> Shift Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-muted-foreground text-xs">Date</p>
                  <p className="font-mono">{timesheet.date}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Scheduled Duration</p>
                  <p className="font-mono font-bold">
                    {(timesheet.base_duration_hours || timesheet.hours_worked || 0).toFixed(2)}h
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Scheduled Start</p>
                  <p className="font-mono">{formatTime(timesheet.base_start_time) || timesheet.start_time || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Scheduled End</p>
                  <p className="font-mono">{formatTime(timesheet.base_end_time) || timesheet.end_time || '-'}</p>
                </div>
                {timesheet.support_type && (
                  <div>
                    <p className="text-muted-foreground text-xs">Support Type</p>
                    <p>{timesheet.support_type}</p>
                  </div>
                )}
                {timesheet.shift_context && (
                  <div>
                    <p className="text-muted-foreground text-xs">Context</p>
                    <p>{timesheet.shift_context}</p>
                  </div>
                )}
              </div>
              {(timesheet.branch_name || timesheet.state_name) && (
                <div className="flex items-center gap-4 pt-2 border-t">
                  {timesheet.branch_name && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Building2 className="h-3 w-3" />
                      {timesheet.branch_name}
                    </div>
                  )}
                  {timesheet.state_name && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {timesheet.state_name}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section B: Attendance Details */}
          <Card data-testid="attendance-details-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Attendance Details
                </span>
                {!editingTimes && (
                  <Button 
                    variant="ghost" size="sm" className="h-7 px-2 text-xs"
                    onClick={() => {
                      setEditingTimes(true);
                      setEditClockIn(timesheet.actual_clock_in_time ? new Date(timesheet.actual_clock_in_time).toISOString().slice(0,16) : '');
                      setEditClockOut(timesheet.actual_clock_out_time ? new Date(timesheet.actual_clock_out_time).toISOString().slice(0,16) : '');
                    }}
                    data-testid="edit-attendance-btn"
                  >
                    <Edit3 className="h-3 w-3 mr-1" /> Edit
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {editingTimes ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Actual Clock In</Label>
                      <Input type="datetime-local" value={editClockIn} onChange={e => setEditClockIn(e.target.value)} data-testid="edit-clock-in" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Actual Clock Out</Label>
                      <Input type="datetime-local" value={editClockOut} onChange={e => setEditClockOut(e.target.value)} data-testid="edit-clock-out" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1" disabled={actionLoading} onClick={() => {
                      onUpdateTimes(timesheet.id, editClockIn ? new Date(editClockIn).toISOString() : null, editClockOut ? new Date(editClockOut).toISOString() : null, '');
                      setEditingTimes(false);
                    }} data-testid="save-times-btn">Save</Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => setEditingTimes(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-muted-foreground text-xs">Actual Clock In</p>
                  <p className="font-mono">{formatTime(timesheet.actual_clock_in_time) || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Actual Clock Out</p>
                  <p className="font-mono">{formatTime(timesheet.actual_clock_out_time) || '-'}</p>
                </div>
              </div>
              )}
              
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-muted-foreground text-xs">Attendance Status</p>
                  <AttendanceBadge status={timesheet.attendance_status} />
                </div>
                {timesheet.variance_minutes !== undefined && timesheet.variance_minutes !== 0 && (
                  <div>
                    <p className="text-muted-foreground text-xs">Variance</p>
                    <p className={`font-mono ${timesheet.variance_minutes > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {formatMinutes(timesheet.variance_minutes)}
                    </p>
                  </div>
                )}
              </div>

              {/* Attendance Flags */}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {timesheet.late_minutes > 0 && (
                  <Badge variant="outline" className="text-yellow-700 border-yellow-300 bg-yellow-50">
                    Late by {timesheet.late_minutes}m
                  </Badge>
                )}
                {timesheet.early_leave_minutes > 0 && (
                  <Badge variant="outline" className="text-orange-700 border-orange-300 bg-orange-50">
                    Left early by {timesheet.early_leave_minutes}m
                  </Badge>
                )}
                {timesheet.overtime_minutes > 0 && (
                  <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-50">
                    Overtime: {timesheet.overtime_minutes}m
                  </Badge>
                )}
                {(timesheet.gps_issue || timesheet.gps_mismatch) && (
                  <Badge variant="outline" className="text-red-700 border-red-300 bg-red-50">
                    <MapPinOff className="h-3 w-3 mr-1" /> GPS Issue
                  </Badge>
                )}
                {(timesheet.manual_edit || timesheet.times_manually_updated) && (
                  <Badge variant="outline" className="text-purple-700 border-purple-300 bg-purple-50">
                    Manually Updated
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Section C: Participant / Support Context */}
          <Card data-testid="participant-context-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="h-4 w-4" /> Participant Context
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-muted-foreground text-xs">Staff</p>
                  <p className="font-medium">{timesheet.staff_name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Primary Participant</p>
                  <p className="font-medium">{timesheet.participant_name || '-'}</p>
                </div>
              </div>

              {/* Linked Participants (for group/SIL services) */}
              {timesheet.linked_participants && timesheet.linked_participants.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground text-xs mb-1">
                    <Users className="h-3 w-3 inline mr-1" />
                    Linked Participants ({timesheet.linked_participants.length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {timesheet.linked_participants.map((p, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {p.name || p}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {timesheet.house_name && (
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground text-xs">House (SIL)</p>
                  <p>{timesheet.house_name}</p>
                </div>
              )}

              {timesheet.service_item && (
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground text-xs">Service Item</p>
                  <p className="text-xs text-muted-foreground">{timesheet.service_item}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section D: Notes / Exception Reason */}
          {(timesheet.staff_notes || timesheet.notes || timesheet.exception_reason || 
            timesheet.correction_notes || timesheet.rejection_reason) && (
            <Card data-testid="notes-section">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Notes & Reasons
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {(timesheet.staff_notes || timesheet.notes) && (
                  <div>
                    <p className="text-muted-foreground text-xs">Staff Notes</p>
                    <p className="bg-muted/50 rounded p-2 text-xs">{timesheet.staff_notes || timesheet.notes}</p>
                  </div>
                )}
                {timesheet.exception_reason && (
                  <div>
                    <p className="text-muted-foreground text-xs">Exception Reason</p>
                    <p className="bg-orange-50 rounded p-2 text-xs text-orange-800">{timesheet.exception_reason}</p>
                  </div>
                )}
                {timesheet.correction_notes && (
                  <div>
                    <p className="text-muted-foreground text-xs">Correction Notes</p>
                    <p className="bg-yellow-50 rounded p-2 text-xs text-yellow-800">{timesheet.correction_notes}</p>
                  </div>
                )}
                {timesheet.rejection_reason && (
                  <div>
                    <p className="text-muted-foreground text-xs">Rejection Reason</p>
                    <p className="bg-red-50 rounded p-2 text-xs text-red-800">{timesheet.rejection_reason}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Section E: Review Status */}
          <Card data-testid="review-status-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <CheckCircle className="h-4 w-4" /> Review Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Approval Status</span>
                <ApprovalBadge status={timesheet.approval_status || timesheet.status} />
              </div>
              {(() => {
                const ts = timesheet;
                const hasSource =
                  ts.auto_submitted_trigger ||
                  ts.auto_submitted_on_clockout === true ||
                  ts.status === 'SUBMITTED';
                if (!hasSource) return null;
                return (
                  <div className="flex items-center justify-between" data-testid="drawer-submission-source-row">
                    <span className="text-muted-foreground">Submission Source</span>
                    <SubmissionSourceBadge timesheet={ts} size="sm" />
                  </div>
                );
              })()}
              {timesheet.version && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Version</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono">v{timesheet.version}</span>
                    {timesheet.is_correction && (
                      <Badge className="bg-purple-600 text-white text-xs">Correction</Badge>
                    )}
                  </div>
                </div>
              )}
              {/* Day 17: Correction Reason Display */}
              {timesheet.correction_reason && (
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground text-xs">Correction Reason</p>
                  <p className="bg-purple-50 rounded p-2 text-xs text-purple-800">{timesheet.correction_reason}</p>
                </div>
              )}
              {timesheet.is_superseded && (
                <Alert className="border-gray-300 bg-gray-50">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-gray-700">
                    This timesheet has been superseded by a newer version.
                  </AlertDescription>
                </Alert>
              )}
              {/* Day 17: Needs Reapproval Alert */}
              {timesheet.approval_status === 'corrected_pending_reapproval' && (
                <Alert className="border-orange-300 bg-orange-50">
                  <RotateCcw className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-800">
                    This timesheet was edited after approval and requires reapproval.
                  </AlertDescription>
                </Alert>
              )}
              {timesheet.approved_duration_hours !== undefined && (
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-muted-foreground">Approved Hours</span>
                  <span className="font-mono font-bold text-green-600">
                    {(timesheet.approved_duration_hours || 0).toFixed(2)}h
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section F: Audit Summary */}
          <Card data-testid="audit-summary-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <History className="h-4 w-4" /> Audit Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {timesheet.approved_by_name && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Approved By</span>
                  <span>{timesheet.approved_by_name}</span>
                </div>
              )}
              {timesheet.approved_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Approved At</span>
                  <span className="font-mono text-xs">{formatDateTime(timesheet.approved_at)}</span>
                </div>
              )}
              {timesheet.updated_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Last Updated</span>
                  <span className="font-mono text-xs">{formatDateTime(timesheet.updated_at)}</span>
                </div>
              )}
              {timesheet.created_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span className="font-mono text-xs">{formatDateTime(timesheet.created_at)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          {canApprove && (
            <Card className="border-primary/20" data-testid="action-buttons-section">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Approval Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stabilisation Blocker #2 — soft funding preview. Always
                    rendered above the action buttons; never blocks approve. */}
                <FundingPreviewBanner timesheetId={timesheet.id} />
                {actionMode === null ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      onClick={() => handleAction('scheduled')} 
                      disabled={actionLoading}
                      data-testid="approve-scheduled-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve Scheduled
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setActionMode('adjusted')}
                      disabled={actionLoading}
                      data-testid="approve-adjusted-btn"
                    >
                      Approve Adjusted
                    </Button>
                    <Button 
                      variant="secondary" 
                      onClick={() => setActionMode('return')}
                      disabled={actionLoading}
                      data-testid="return-btn"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Return for Correction
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={() => setActionMode('reject')}
                      disabled={actionLoading}
                      data-testid="reject-btn"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                    {/* Update Actual Times — available for any timesheet with attendance data */}
                    <Button 
                      variant="outline" 
                      className="col-span-2"
                      onClick={() => setActionMode('update-times')}
                      disabled={actionLoading}
                      data-testid="update-times-btn"
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      Update Actual Times
                    </Button>
                    {/* Day 17: Edit After Approval Button */}
                    {(timesheet.approval_status === 'approved_scheduled' || 
                      timesheet.approval_status === 'approved_adjusted' ||
                      timesheet.approval_status === 'reapproved' ||
                      timesheet.approval_status === 'APPROVED') && (
                      <Button 
                        variant="outline" 
                        className="col-span-2"
                        onClick={() => setActionMode('edit-after-approval')}
                        disabled={actionLoading}
                        data-testid="edit-after-approval-btn"
                      >
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit Approved Timesheet
                      </Button>
                    )}
                    {/* Day 17: Reapprove Button */}
                    {timesheet.approval_status === 'corrected_pending_reapproval' && (
                      <Button 
                        className="col-span-2 bg-green-600 hover:bg-green-700"
                        onClick={() => setActionMode('reapprove')}
                        disabled={actionLoading}
                        data-testid="reapprove-btn"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reapprove Corrected Timesheet
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {actionMode === 'adjusted' && (
                      <div className="space-y-2">
                        <Label>Adjusted Hours</Label>
                        <Input
                          type="number"
                          step="0.25"
                          value={adjustedHours}
                          onChange={(e) => setAdjustedHours(e.target.value)}
                          placeholder="Enter adjusted hours"
                          data-testid="adjusted-hours-input"
                        />
                      </div>
                    )}
                    {actionMode === 'update-times' && (
                      <>
                        <div className="space-y-2">
                          <Label>Actual Clock In</Label>
                          <Input
                            type="datetime-local"
                            value={actualClockIn ? actualClockIn.slice(0, 16) : ''}
                            onChange={(e) => setActualClockIn(e.target.value ? new Date(e.target.value).toISOString() : '')}
                            data-testid="clock-in-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Actual Clock Out</Label>
                          <Input
                            type="datetime-local"
                            value={actualClockOut ? actualClockOut.slice(0, 16) : ''}
                            onChange={(e) => setActualClockOut(e.target.value ? new Date(e.target.value).toISOString() : '')}
                            data-testid="clock-out-input"
                          />
                        </div>
                      </>
                    )}
                    <div className="space-y-2">
                      <Label>
                        {actionMode === 'reject' ? 'Rejection Reason (Required)' :
                         actionMode === 'return' ? 'Correction Notes (Required)' :
                         'Notes (Optional)'}
                      </Label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={
                          actionMode === 'reject' ? 'e.g. "Shift not attended — staff confirmed via SMS"; "Duplicate of TS-44219"; "Hours exceed funded daily allowance"' :
                          actionMode === 'return' ? 'e.g. "Clock-out missing — please add actual end time"; "Progress note required for incident shift"; "Adjust break to 30 min unpaid"' :
                          'Add any notes...'
                        }
                        rows={3}
                        data-testid="action-notes"
                        onKeyDown={(e) => {
                          if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                            e.preventDefault();
                            const blocked = (actionLoading || editLoading ||
                              ((actionMode === 'reject' || actionMode === 'return') && !notes.trim()));
                            if (!blocked) handleAction(actionMode);
                          }
                        }}
                      />
                      {(actionMode === 'reject' || actionMode === 'return') && (
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>
                            {notes.trim().length === 0
                              ? 'Reason is required.'
                              : notes.trim().length < 10
                                ? 'Add a bit more detail (min 10 chars recommended).'
                                : 'Looks good. Press Ctrl/⌘+Enter to submit.'}
                          </span>
                          <span data-testid="action-notes-counter">{notes.length} chars</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setActionMode(null)} disabled={actionLoading || editLoading}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={() => handleAction(actionMode)}
                        disabled={actionLoading || editLoading || 
                          ((actionMode === 'reject' || actionMode === 'return') && !notes.trim()) ||
                          (actionMode === 'edit-after-approval' && editReason.length < 10)}
                        variant={actionMode === 'reject' ? 'destructive' : 
                          (actionMode === 'reapprove' ? 'default' : 'default')}
                        className={actionMode === 'reapprove' ? 'bg-green-600 hover:bg-green-700' : ''}
                      >
                        {(actionLoading || editLoading) ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                        ) : (
                          actionMode === 'adjusted' ? 'Approve Adjusted' :
                          actionMode === 'reject' ? 'Reject Timesheet' :
                          actionMode === 'return' ? 'Return for Correction' :
                          actionMode === 'edit-after-approval' ? 'Apply Correction' :
                          actionMode === 'reapprove' ? 'Confirm Reapproval' :
                          'Update Times'
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Day 17: Edit After Approval Form */}
                {actionMode === 'edit-after-approval' && (
                  <div className="space-y-4">
                    <Alert className="border-orange-200 bg-orange-50">
                      <AlertTriangle className="h-4 w-4 text-orange-600" />
                      <AlertDescription className="text-orange-800">
                        Editing will change status to "Needs Reapproval" and create a new version.
                      </AlertDescription>
                    </Alert>
                    <div className="space-y-2">
                      <Label>Hours Worked</Label>
                      <Input
                        type="number"
                        step="0.25"
                        value={editHours}
                        onChange={(e) => setEditHours(e.target.value)}
                        placeholder="Enter hours worked"
                        data-testid="edit-hours-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Edit Reason (min 10 chars, required)</Label>
                      <Textarea
                        value={editReason}
                        onChange={(e) => setEditReason(e.target.value)}
                        placeholder="Explain why this correction is needed..."
                        rows={2}
                        className={cn(editReason.length > 0 && editReason.length < 10 && "border-red-500")}
                        data-testid="edit-reason-input"
                      />
                      <p className="text-xs text-muted-foreground">{editReason.length}/10</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes (Optional)</Label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any additional notes..."
                        rows={2}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setActionMode(null)} disabled={editLoading}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={() => handleAction('edit-after-approval')}
                        disabled={editLoading || editReason.length < 10}
                        data-testid="apply-edit-btn"
                      >
                        {editLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Edit3 className="h-4 w-4 mr-2" />}
                        Apply Correction
                      </Button>
                    </div>
                  </div>
                )}

                {/* Day 17: Reapprove Form */}
                {actionMode === 'reapprove' && (
                  <div className="space-y-4">
                    <Alert className="border-green-200 bg-green-50">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-800">
                        This corrected timesheet is ready for reapproval.
                      </AlertDescription>
                    </Alert>
                    <Card className="bg-muted/50">
                      <CardContent className="p-3 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Hours</span>
                          <span className="font-mono font-bold">{(timesheet.hours_worked || 0).toFixed(2)}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Version</span>
                          <span className="font-mono">v{timesheet.version || 1}</span>
                        </div>
                        {timesheet.correction_reason && (
                          <div className="pt-2 border-t">
                            <p className="text-xs text-muted-foreground">Correction Reason</p>
                            <p className="text-xs bg-purple-50 rounded p-2 text-purple-800">{timesheet.correction_reason}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    <div className="space-y-2">
                      <Label>Approval Notes (Optional)</Label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add any reapproval notes..."
                        rows={2}
                        data-testid="reapproval-notes"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setActionMode(null)} disabled={editLoading}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={() => handleAction('reapprove')}
                        disabled={editLoading}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="confirm-reapproval-btn"
                      >
                        {editLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                        Confirm Reapproval
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function TimesheetApprovalV2Page() {
  const { user, permissions } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [stats, setStats] = useState({ pending_approval: 0, attendance_exceptions: 0, approved_today: 0, returned: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [exceptionFilter, setExceptionFilter] = useState('');
  
  // Sort state (Day 28 polish)
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  
  // Reference data
  const [branches, setBranches] = useState([]);
  const [states, setStates] = useState([]);
  
  // Detail drawer
  const [selectedTs, setSelectedTs] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Feature flag
  const [dashboardEnabled, setDashboardEnabled] = useState(true); // Default show until we check

  // Check permission
  const hasApprovalPermission = permissions?.includes('timesheets.approve') || 
    ['Super Admin', 'Admin', 'HR', 'Accounts', 'Payroll Manager'].includes(user?.role_name);

  // Fetch feature flag and reference data on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get('/timesheets/v2/config');
        setDashboardEnabled(res.data.data?.timesheet_approval_dashboard_enabled ?? true);
      } catch (e) {
        // Default to enabled if config fails
      }
    };
    
    const fetchBranches = async () => {
      try {
        const res = await api.get('/org/branches');
        setBranches(res.data.data || []);
      } catch (e) {}
    };
    
    const fetchStates = async () => {
      try {
        const res = await api.get('/org/states');
        setStates(res.data.data || []);
      } catch (e) {}
    };
    
    fetchConfig();
    fetchBranches();
    fetchStates();
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/timesheets/v2/stats/summary');
      setStats(res.data.data || {});
    } catch (e) {
      console.error('Failed to load stats', e);
    }
  }, []);

  const fetchTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/timesheets/v2/pending';
      const params = new URLSearchParams();
      
      if (activeTab === 'all') {
        url = '/timesheets/v2/all';
        if (statusFilter) params.append('status_filter', statusFilter);
      } else if (activeTab === 'exceptions') {
        url = '/timesheets/v2/exceptions';
      } else if (activeTab === 'returned') {
        url = '/timesheets/v2/all';
        params.append('status_filter', 'returned_for_correction');
      }
      
      // Add filters
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (branchFilter) params.append('branch_id', branchFilter);
      if (stateFilter) params.append('state_id', stateFilter);
      if (exceptionFilter) params.append('attendance_status', exceptionFilter);
      
      const queryString = params.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;
      
      const res = await api.get(finalUrl);
      setTimesheets(res.data.items || []);
    } catch (e) {
      notify.error(null, 'TIMESHEET_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  }, [activeTab, statusFilter, dateFrom, dateTo, branchFilter, stateFilter, exceptionFilter]);

  useEffect(() => {
    fetchStats();
    fetchTimesheets();
  }, [fetchStats, fetchTimesheets]);

  const filteredTimesheets = React.useMemo(() => {
    const filtered = timesheets.filter(ts => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        ts.staff_name?.toLowerCase().includes(s) ||
        ts.participant_name?.toLowerCase().includes(s) ||
        ts.date?.includes(s) ||
        ts.house_name?.toLowerCase().includes(s) ||
        ts.shift_id?.toLowerCase().includes(s)
      );
    });
    // Day 28 polish — column sort
    const getVal = (ts, f) => {
      switch (f) {
        case 'date': return ts.date || '';
        case 'staff': return (ts.staff_name || '').toLowerCase();
        case 'participant': return (ts.participant_name || '').toLowerCase();
        case 'scheduled': return Number(ts.base_duration_hours || ts.hours_worked || 0);
        case 'actual': return Number(ts.actual_duration_minutes || 0);
        case 'variance': return Number(ts.variance_minutes || 0);
        case 'status': return (ts.approval_status || ts.status || '').toLowerCase();
        default: return '';
      }
    };
    const sorted = [...filtered].sort((a, b) => {
      const va = getVal(a, sortField);
      const vb = getVal(b, sortField);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [timesheets, search, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleRowClick = (ts) => {
    setSelectedTs(ts);
    setDrawerOpen(true);
  };

  // Approval handlers
  const handleApproveScheduled = async (id, notes) => {
    setActionLoading(true);
    try {
      await api.post(`/timesheets/v2/${id}/approve-scheduled`, { notes });
      toast.success('Timesheet approved with scheduled hours');
      setDrawerOpen(false);
      fetchTimesheets();
      fetchStats();
    } catch (e) {
      notify.error(e, 'TIMESHEET_APPROVAL_FAILED');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveAdjusted = async (id, hours, notes) => {
    setActionLoading(true);
    try {
      await api.post(`/timesheets/v2/${id}/approve-adjusted`, { 
        adjusted_duration_hours: hours,
        notes 
      });
      toast.success(`Timesheet approved with ${hours}h`);
      setDrawerOpen(false);
      fetchTimesheets();
      fetchStats();
    } catch (e) {
      notify.error(e, 'TIMESHEET_APPROVAL_FAILED');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id, reason) => {
    setActionLoading(true);
    try {
      await api.post(`/timesheets/v2/${id}/reject`, { reason });
      toast.success('Timesheet rejected');
      setDrawerOpen(false);
      fetchTimesheets();
      fetchStats();
    } catch (e) {
      notify.error(e, 'TIMESHEET_APPROVAL_FAILED');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReturn = async (id, notes) => {
    setActionLoading(true);
    try {
      await api.post(`/timesheets/v2/${id}/return`, { correction_notes: notes });
      toast.success('Timesheet returned for correction');
      setDrawerOpen(false);
      fetchTimesheets();
      fetchStats();
    } catch (e) {
      notify.error(e, 'TIMESHEET_APPROVAL_FAILED');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateTimes = async (id, clockIn, clockOut, notes) => {
    setActionLoading(true);
    try {
      await api.post(`/timesheets/v2/${id}/update-actual-times`, { 
        actual_clock_in_time: clockIn || null,
        actual_clock_out_time: clockOut || null,
        notes 
      });
      toast.success('Actual times updated');
      setDrawerOpen(false);
      fetchTimesheets();
      fetchStats();
    } catch (e) {
      notify.error(e, 'TIMESHEET_EDIT_FAILED');
    } finally {
      setActionLoading(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setBranchFilter('');
    setStateFilter('');
    setExceptionFilter('');
    setSearch('');
  };

  const hasActiveFilters = statusFilter || dateFrom || dateTo || branchFilter || stateFilter || exceptionFilter;

  // Permission check
  if (!hasApprovalPermission) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">
              You do not have permission to access the timesheet approval dashboard.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="timesheet-approval-v2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Timesheet Approval Dashboard</h1>
          <p className="text-muted-foreground mt-1">Review and approve staff timesheets</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className={cn(hasActiveFilters && 'border-primary text-primary')}
          data-testid="toggle-filters"
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {hasActiveFilters && <Badge className="ml-2 h-5 w-5 p-0 justify-center">!</Badge>}
        </Button>
      </div>

      {/* Stats Cards + Today's Submissions Widget */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-yellow-500 border-l-4 cursor-pointer hover:bg-muted/50 transition-colors" 
                onClick={() => setActiveTab('pending')} data-testid="stats-pending">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Approval</p>
                  <p className="text-2xl font-bold">{stats.pending_approval || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-500 border-l-4 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setActiveTab('exceptions')} data-testid="stats-exceptions">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Exceptions</p>
                  <p className="text-2xl font-bold">{stats.attendance_exceptions || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-500 border-l-4 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setActiveTab('returned')} data-testid="stats-returned">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Returned</p>
                  <p className="text-2xl font-bold">{stats.returned || 0}</p>
                </div>
                <RotateCcw className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-500 border-l-4" data-testid="stats-approved">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Approved Today</p>
                  <p className="text-2xl font-bold">{stats.approved_today || 0}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="lg:col-span-1">
          <TodaysSubmissionsWidget />
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <Card className="border-dashed" data-testid="filters-panel">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Date Range</Label>
                <DateRangePicker
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  onDateFromChange={setDateFrom}
                  onDateToChange={setDateTo}
                />
              </div>
              
              <div className="space-y-1 min-w-[150px]">
                <Label className="text-xs">Branch</Label>
                <Select value={branchFilter || "all"} onValueChange={(val) => setBranchFilter(val === "all" ? "" : val)}>
                  <SelectTrigger data-testid="branch-filter">
                    <SelectValue placeholder="All Branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1 min-w-[150px]">
                <Label className="text-xs">State</Label>
                <Select value={stateFilter || "all"} onValueChange={(val) => setStateFilter(val === "all" ? "" : val)}>
                  <SelectTrigger data-testid="state-filter">
                    <SelectValue placeholder="All States" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All States</SelectItem>
                    {states.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name || s.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1 min-w-[180px]">
                <Label className="text-xs">Exception Type</Label>
                <Select value={exceptionFilter || "all"} onValueChange={(val) => setExceptionFilter(val === "all" ? "" : val)}>
                  <SelectTrigger data-testid="exception-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {EXCEPTION_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters">
                  Clear All
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending ({stats.pending_approval || 0})
            </TabsTrigger>
            <TabsTrigger value="exceptions" data-testid="tab-exceptions">
              Exceptions ({stats.attendance_exceptions || 0})
            </TabsTrigger>
            <TabsTrigger value="returned" data-testid="tab-returned">
              Returned ({stats.returned || 0})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-3">
            <div className="relative w-[280px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search staff, participant, date, house..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
                className="pl-9"
                data-testid="timesheet-search"
              />
            </div>
            {activeTab === 'all' && (
              <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending_approval">Pending</SelectItem>
                  <SelectItem value="approved_scheduled">Approved (Scheduled)</SelectItem>
                  <SelectItem value="approved_adjusted">Approved (Adjusted)</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="returned_for_correction">Returned</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <TabsContent value={activeTab}>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px] cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('date')} data-testid="sort-date">
                      Date {sortField === 'date' && (sortDir === 'asc' ? '▲' : '▼')}
                    </TableHead>
                    <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('staff')} data-testid="sort-staff">
                      Staff {sortField === 'staff' && (sortDir === 'asc' ? '▲' : '▼')}
                    </TableHead>
                    <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('participant')} data-testid="sort-participant">
                      Participant {sortField === 'participant' && (sortDir === 'asc' ? '▲' : '▼')}
                    </TableHead>
                    <TableHead className="text-center cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('scheduled')} data-testid="sort-scheduled">
                      Scheduled {sortField === 'scheduled' && (sortDir === 'asc' ? '▲' : '▼')}
                    </TableHead>
                    <TableHead className="text-center cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('actual')} data-testid="sort-actual">
                      Actual {sortField === 'actual' && (sortDir === 'asc' ? '▲' : '▼')}
                    </TableHead>
                    <TableHead className="text-center cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('variance')} data-testid="sort-variance">
                      Variance {sortField === 'variance' && (sortDir === 'asc' ? '▲' : '▼')}
                    </TableHead>
                    <TableHead>Attendance</TableHead>
                    <TableHead>Flags</TableHead>
                    <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort('status')} data-testid="sort-status">
                      Status {sortField === 'status' && (sortDir === 'asc' ? '▲' : '▼')}
                    </TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : filteredTimesheets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                        No timesheets found
                      </TableCell>
                    </TableRow>
                  ) : filteredTimesheets.map(ts => (
                    <TableRow 
                      key={ts.id} 
                      data-testid={`timesheet-row-${ts.id}`} 
                      className="hover:bg-muted/50 cursor-pointer"
                      onClick={() => handleRowClick(ts)}
                    >
                      <TableCell className="font-mono text-xs">
                        <div className="flex flex-col gap-0.5">
                          <span>{ts.date}</span>
                          {(() => {
                            const stored = ts.shift_period;
                            const code = stored || autoClassifyPeriod(ts.base_start_time || ts.start_time, ts.base_end_time || ts.end_time);
                            return <ShiftPeriodBadge period={code} auto={!stored} showIcon={false} />;
                          })()}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{ts.staff_name || 'N/A'}</TableCell>
                      <TableCell>
                        <div>
                          {ts.participant_name || 'N/A'}
                          {ts.linked_participants && ts.linked_participants.length > 0 && (
                            <Badge variant="outline" className="ml-1 text-xs">
                              +{ts.linked_participants.length}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center font-mono">
                        {(ts.base_duration_hours || ts.hours_worked || 0).toFixed(2)}h
                      </TableCell>
                      <TableCell className="text-center font-mono">
                        {ts.actual_duration_minutes ? formatMinutes(ts.actual_duration_minutes) : '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        {ts.variance_minutes && ts.variance_minutes !== 0 ? (
                          <span className={`font-mono ${ts.variance_minutes > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                            {formatMinutes(ts.variance_minutes)}
                          </span>
                        ) : (
                          <span className="text-green-600">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <AttendanceBadge status={ts.attendance_status} />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <ExceptionIndicators timesheet={ts} />
                          <PayrollBadges timesheet={ts} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <ApprovalBadge status={ts.approval_status || ts.status} />
                          <SubmissionSourceBadge timesheet={ts} showIcon={false} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Drawer */}
      <TimesheetDetailDrawer
        timesheet={selectedTs}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onApproveScheduled={handleApproveScheduled}
        onApproveAdjusted={handleApproveAdjusted}
        onReject={handleReject}
        onReturn={handleReturn}
        onUpdateTimes={handleUpdateTimes}
        actionLoading={actionLoading}
      />
    </div>
  );
}
